import React, { useContext, useEffect, useState } from 'react';
import { withErrorBoundary, Loader, COMMON_CONSTANTS } from '@nile/components';
import {
  NUMBER,
  SUMMARY_TABLE,
  DEPARTMENT,
  SITE,
  NUMBER_CHART
} from '@nile/reports/src/modules/charts/constants';
import { PREVIEW_CHART } from '@nile/reports/src/modules/charts/graphql';
import EChart from '@nile/reports/src/modules/charts/components/subpages/EChart';
import NumberChart from '@nile/reports/src/modules/charts/components/subpages/NumberChart';
import NumberCard from '@nile/reports/src/modules/charts/components/subpages/NumberCard';
import SummaryTable from '@nile/reports/src/modules/charts/components/subpages/SummaryTable';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import {
  AccountClient,
  getNonAuthToken,
  NonAuthAccountClient
} from '@nile/graphql';
import { VIEW_MODE } from '@nile/reports/src/modules/home/constants';
import { preFormatFilterObject } from '@nile/reports/src/modules/component/helper';
import {
  getDefaultChartType,
  getPossibleChartTypes,
  getTransformedChartData
} from '@nile/reports/src/modules/charts/utils/chart-transformation';
import { cloneDeep, isEmpty } from 'lodash';
import InsightsContext from '@nile/itsm/src/pages/insights/contexts/Insights';
import { GET_COMPANY_SITE } from '@nile/itsm/src/graphql';
import SiteInsight from '@nile/itsm/src/pages/insights/Layout/widgets/SiteInsight';
import DepartmentInsight from '@nile/itsm/src/pages/insights/Layout/widgets/DepartmentInsight';

function RenderChart(props) {
  const {
    item,
    size,
    queryVariables,
    isNumberCard = false,
    insightChartData = null,
    numberChartCallback,
    numberChartTooltip
  } = props;

  const reportsContext = useContext(ReportsContext);
  const insightsContext = useContext(InsightsContext);

  const { chart } = item;

  const [chartOption, setChartOption] = useState();
  const [chartRawData, setChartRawData] = useState({});

  const [chartType, setChartType] = useState();

  const [loading, setLoading] = useState(true);

  const transformData = async (chartData) => {
    return getTransformedChartData(
      chartData,
      chart.metrics?.length,
      chart.groupBy?.length,
      chart.defaultChartType,
      chart
    );
  };

  const getChartOptions = async (id, chart) => {
    let requestInput = {
      chart: {
        chartId: id
      }
    };
    if (reportsContext.reportViewMode === VIEW_MODE) {
      const { operands, reportCondition } = await preFormatFilterObject({
        entity: chart?.entity,
        filterObj: cloneDeep(
          reportsContext.reportFilter?.current &&
            reportsContext.reportFilter?.current?.[reportsContext.activeView]
        )
      });
      requestInput = {
        ...requestInput,
        condition:
          (reportCondition &&
            !isEmpty(operands) && {
              joinOperator: 'AND',
              operands
            }) ||
          null
      };
    } else if (queryVariables) {
      requestInput.condition = queryVariables?.condition;
    }

    // if non auth token is present use non auth client
    const nonAuthToken = getNonAuthToken();
    const previewChartClient = nonAuthToken
      ? NonAuthAccountClient
      : AccountClient;

    await previewChartClient
      .query({
        query: PREVIEW_CHART,
        variables: {
          PreviewChartInput: requestInput
        },
        fetchPolicy: 'network-only'
      })
      .then(({ data }) => {
        if (data?.previewChart) {
          setChartRawData(data?.previewChart);
          const transformed = transformData(data?.previewChart);
          transformed
            .then((formatteddata) => setChartOption(formatteddata))
            .catch((err) => {})
            .finally(() => {
              setLoading(false);
              if (reportsContext?.setRefreshWidget)
                reportsContext?.setRefreshWidget(false);
            });
        } else {
          setChartOption();
          setLoading(false);
          setLoading(false);
        }
      })
      .catch((err: any) => {
        setLoading(false);
      });
  };
  const skipCompanySite = (siteWidget) => {
    const siteId = siteWidget?.data?.id;
    // ignoring invalid site ids
    if (!siteId || siteId === '16' || siteId === '17') {
      return true;
    }
    return false;
  };

  const getSiteWidget = async (siteWidget) => {
    if (skipCompanySite(siteWidget)) {
      return;
    }
    await AccountClient.query({
      query: GET_COMPANY_SITE,
      variables: {
        input: { id: siteWidget?.data?.id }
      },
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    })
      .then(({ data }) => {
        if (data) {
          setChartOption(data.getCompanySite);
          setLoading(false);
        } else {
          setChartOption();
          setLoading(false);
        }
      })
      .catch((err: any) => {
        setLoading(false);
      });
  };

  const getChartType = () => {
    const type =
      (getPossibleChartTypes(chart.metrics?.length, chart.groupBy?.length).find(
        (type) => type.name === chart.defaultChartType
      ) &&
        chart.defaultChartType) ||
      getDefaultChartType(chart.metrics?.length, chart.groupBy?.length);
    setChartType(type);
  };

  useEffect(() => {
    if (!isEmpty(chart)) {
      setLoading(true);
      getChartType();
      if (
        chart.defaultChartType !== DEPARTMENT &&
        chart.defaultChartType !== SITE
      ) {
        getChartOptions(chart.chartId, chart);
      } else if (insightChartData) {
        if (insightChartData.type == DEPARTMENT && insightChartData.data) {
          setLoading(false);
        } else {
          getSiteWidget(insightChartData);
        }
      }
    }
  }, [JSON.stringify(chart), reportsContext.reportViewMode]);

  useEffect(() => {
    if (reportsContext.reportCondition) {
      setLoading(true);
      getChartOptions(chart?.chartId, chart);
    }
  }, [reportsContext.reportCondition]);

  useEffect(() => {
    if (reportsContext.refreshWidget) {
      setLoading(true);
      getChartOptions(chart.chartId, chart);
    }
  }, [reportsContext.refreshWidget]);

  useEffect(() => {
    setLoading(true);
    if (
      chart.defaultChartType !== DEPARTMENT &&
      chart.defaultChartType !== SITE
    ) {
      getChartOptions(chart.chartId, chart);
    } else if (insightChartData) {
      if (insightChartData.type == DEPARTMENT && insightChartData.data) {
        setLoading(false);
      } else {
        getSiteWidget(insightChartData);
      }
    }
  }, [insightsContext?.filterConditions, insightChartData]);

  if (loading || chartOption === 1) {
    return (
      <div className="flex full-height">
        <Loader />
      </div>
    );
  }

  if (chart.defaultChartType == DEPARTMENT) {
    return <DepartmentInsight chartData={insightChartData} />;
  }

  if (chart.defaultChartType == SITE) {
    return <SiteInsight data={chartOption} />;
  }

  if (chartType === NUMBER && !isNumberCard) {
    return (
      <NumberChart
        btnCallback={(index) => {
          numberChartCallback({ data: chart, index });
        }}
        data={chartOption}
        type={NUMBER_CHART.SUB_TYPE.BUTTON}
        tooltipContent={numberChartTooltip}
      />
    );
  }

  if (chartType === NUMBER && isNumberCard) {
    return (
      <>
        <NumberCard data={chartOption} chart={chart} />
      </>
    );
  }
  if (chartType === SUMMARY_TABLE) {
    return (
      <SummaryTable
        data={chartOption}
        groups={chart.groupBy}
        metrics={chart.metrics}
        entity={chart.entity}
        chartRawData={chartRawData}
      />
    );
  }
  return (
    <EChart
      chartOptions={chartOption}
      internalName={chart?.internalName}
      chartType={chart?.defaultChartType}
      reportType={reportsContext?.activeReport?.reportType}
      chart={chart}
      opts={{ height: size.height, width: size.width, renderer: 'svg' }}
    />
  );
}

export default withErrorBoundary(RenderChart);
